import { useState, useCallback, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { STONE_TYPES } from '@/utils/cad-sheet';
import { Plus, Trash2, Camera, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseStonesScreenshot, formatApiError } from '@/utils/api';

const FIELDS = ['type', 'ownership', 'cut', 'category', 'quality', 'size', 'weight', 'quantity'];
const HEADERS = ['Stone', 'Ownership', 'Cut', 'Category', 'Quality', 'Size', 'Ct. Wt.', 'Qty', 'Total Wt'];

const emptyRow = () => ({
  key: crypto.randomUUID(),
  type: '', ownership: '', cut: '', category: '', quality: '', weight: '', quantity: '', size: '',
});

const stoneToRow = (s) => ({
  key: crypto.randomUUID(),
  type: s.type, ownership: s.ownership || '', cut: s.cut, category: s.category || '',
  quality: s.quality, weight: s.weight, quantity: s.quantity, size: s.size,
});

const preprocessImage = (base64Data) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Scale up by 2x for better OCR recognition on small decimal points
      canvas.width = img.width * 2;
      canvas.height = img.height * 2;
      
      // Draw image scaled
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Grayscale + Binarization (thresholding at 150)
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imgData.data;
      for (let i = 0; i < data.length; i += 4) {
        const grayscale = 0.3 * data[i] + 0.59 * data[i + 1] + 0.11 * data[i + 2];
        const val = grayscale > 150 ? 255 : 0;
        
        data[i] = val;
        data[i + 1] = val;
        data[i + 2] = val;
      }
      ctx.putImageData(imgData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(base64Data);
    img.src = base64Data;
  });
};

const loadTesseract = () => {
  return new Promise((resolve, reject) => {
    if (window.Tesseract) {
      resolve(window.Tesseract);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js';
    script.onload = () => resolve(window.Tesseract);
    script.onerror = () => reject(new Error('Failed to load local OCR library (Tesseract.js). Check your internet connection.'));
    document.head.appendChild(script);
  });
};

const parseOcrText = (text) => {
  if (!text) return [];
  const lines = text.split('\n');
  const extractedStones = [];

  const stoneTypes = ['Diamond', 'Ruby', 'Sapphire', 'Emerald', 'Gemstone', 'Cubic Zirconia', 'Moissanite', 'Pearl'];
  const cuts = ['Round', 'Princess', 'Oval', 'Pear', 'Marquise', 'Cushion', 'Baguette', 'Emerald', 'Radiant', 'Heart', 'Trillion'];
  const categories = ['Accent Stone', 'Center Stone', 'Side Stone'];
  const ownerships = ['Ours/Cust', 'KS Goods', 'Customer Goods'];

  // Look for total gem weight in the entire text (e.g. "Total Gem Weight .19")
  let docTotalWeight = null;
  const totalWeightRegex = /Total\s+(?:Gem\s+)?Weight\s*[:,-]?\s*(\.\d+|\d+(?:\.\d+)?)/i;
  const totalWeightMatch = text.match(totalWeightRegex);
  if (totalWeightMatch) {
    let wStr = totalWeightMatch[1];
    if (wStr.startsWith('.')) {
      wStr = '0' + wStr;
    }
    let val = parseFloat(wStr);
    if (!wStr.includes('.') && val >= 10) {
      val = val / 100;
    }
    docTotalWeight = val;
  }

  // Global document scan for fallback defaults
  let docDefaultType = 'Diamond';
  let docDefaultCut = 'Round';
  const lowerText = text.toLowerCase();
  for (const t of stoneTypes) {
    if (lowerText.includes(t.toLowerCase())) {
      docDefaultType = t;
      break;
    }
  }
  for (const c of cuts) {
    if (lowerText.includes(c.toLowerCase())) {
      docDefaultCut = c;
      break;
    }
  }

  // Metadata/header/footer lines to ignore to avoid parsing fake rows
  const ignoreTerms = ['total', 'count', 'weight', 'report', 'update', 'material', 'spg', 'reporter', 'page', 'date', 'designer', 'style'];

  for (let line of lines) {
    line = line.trim();
    if (!line) continue;

    const lowerLine = line.toLowerCase();
    
    // Skip headers and summary lines
    const shouldIgnore = ignoreTerms.some(term => {
      const regex = new RegExp('\\b' + term + '\\b', 'i');
      return regex.test(lowerLine);
    });
    if (shouldIgnore) continue;

    // Find stone type (fall back to document default if not mentioned in line)
    let type = docDefaultType;
    for (const t of stoneTypes) {
      if (lowerLine.includes(t.toLowerCase())) {
        type = t;
        break;
      }
    }

    // Find cut/shape (fall back to document default if not mentioned in line)
    let cut = docDefaultCut;
    for (const c of cuts) {
      if (lowerLine.includes(c.toLowerCase())) {
        cut = c;
        break;
      }
    }

    // Find ownership
    let ownership = 'Ours/Cust';
    for (const o of ownerships) {
      if (lowerLine.includes(o.toLowerCase())) {
        ownership = o;
        break;
      }
    }

    // Find category
    let category = 'Accent Stone';
    for (const cat of categories) {
      if (lowerLine.includes(cat.toLowerCase())) {
        category = cat;
        break;
      }
    }

    // Extract Size (like 1.5mm, 1.5 X 1.5, 1.5*1.5)
    let size = '';
    const sizeRegex = /\b\d+(?:\.\d+)?\s*(?:[xX*]\s*\d+(?:\.\d+)?\s*)*(?:mm)?\b/g;
    const sizeMatches = line.match(sizeRegex);
    if (sizeMatches) {
      for (const match of sizeMatches) {
        if (match.toLowerCase().includes('mm') || match.toLowerCase().includes('x') || match.toLowerCase().includes('*')) {
          size = match.trim();
          break;
        }
      }
      if (!size && sizeMatches.length > 0) {
        for (const match of sizeMatches) {
          const val = parseFloat(match);
          if (val > 0.5 && val < 25.0) {
            size = match.trim() + 'mm';
            break;
          }
        }
      }
    }

    // Format size to restore missing decimals (e.g. 190X190 -> 1.90 X 1.90)
    if (size) {
      const parts = size.split(/\s*([xX*])\s*/);
      const formattedParts = parts.map(part => {
        if (part.toLowerCase() === 'x' || part === '*') return ' X ';
        
        let numStr = part.replace(/mm/i, '').trim();
        let hasMm = part.toLowerCase().includes('mm');
        let val = parseFloat(numStr);
        if (!isNaN(val) && !numStr.includes('.') && val >= 10) {
          if (val >= 100) {
            numStr = (val / 100).toFixed(2);
          } else {
            numStr = (val / 10).toFixed(2);
          }
        }
        return numStr + (hasMm ? 'mm' : '');
      });
      size = formattedParts.join('').trim();
    }

    // Check for total weight notation like "0.19tw", "0.19 tw"
    let isTotalWeight = false;
    let totalWeightVal = 0;
    const twRegex = /(\b\d+(?:\.\d+)?)\s*(?:tw|ctw|tcw)\b/i;
    const twMatch = line.match(twRegex);
    if (twMatch) {
      isTotalWeight = true;
      let rawWeightStr = twMatch[1];
      let val = parseFloat(rawWeightStr);
      // Auto-correct missing decimal point (e.g. "50tw" -> "0.50tw")
      if (!rawWeightStr.includes('.') && val >= 10) {
        val = val / 100;
      }
      totalWeightVal = val;
    }

    // Extract Quantities and Weights
    const numRegex = /\b\d+(?:\.\d+)?\b/g;
    const allNumbers = line.match(numRegex) || [];
    let quantity = '';
    let weight = '';

    const sizeNums = size ? (size.match(numRegex) || []) : [];
    const remainingNumbers = allNumbers.filter(n => !sizeNums.includes(n));

    // Get Quantity (integers)
    for (const numStr of remainingNumbers) {
      if (!numStr.includes('.')) {
        const val = parseInt(numStr, 10);
        if (val > 0 && !quantity) {
          quantity = numStr;
          break;
        }
      }
    }

    // Get Weight
    if (isTotalWeight && quantity) {
      // Calculate per-stone weight from total weight
      weight = (totalWeightVal / parseInt(quantity)).toFixed(4);
    } else {
      for (const numStr of remainingNumbers) {
        if (numStr.includes('.')) {
          const val = parseFloat(numStr);
          if (val < 15.0 && !weight) {
            weight = numStr;
            break;
          }
        }
      }
    }

    // If we have a quantity but no weight, and there are still other remaining numbers
    if (quantity && !weight) {
      const unusedNumStr = remainingNumbers.find(n => n !== quantity);
      if (unusedNumStr) {
        let val = parseFloat(unusedNumStr);
        if (!isNaN(val)) {
          // If it has no decimal point and is >= 10, auto-correct it (e.g. 50 -> 0.50)
          if (!unusedNumStr.includes('.') && val >= 10) {
            weight = (val / 100).toFixed(2);
          } else {
            weight = unusedNumStr;
          }
        }
      }
    }

    // Fallbacks if we only have one remaining number
    if (!weight && !quantity && remainingNumbers.length === 1) {
      const numStr = remainingNumbers[0];
      if (numStr.includes('.')) {
        weight = numStr;
      } else {
        quantity = numStr;
      }
    }

    // Find quality/clarity (VS1, VS2, VS, SI1, SI2, SI, VVS1, VVS2, VVS, I1, I2, I3)
    let quality = '';
    const qualityRegex = /\b(VVS[1-2]?|VS[1-2]?|SI[1-2]?|I[1-3])\b/i;
    const qualMatch = line.match(qualityRegex);
    if (qualMatch) {
      quality = qualMatch[0].toUpperCase();
    }

    if (size || weight || quantity) {
      extractedStones.push({
        type,
        ownership,
        cut,
        category,
        quality,
        size,
        weight,
        quantity
      });
    }
  }

  // Override/correct single row total weight calculation using document-level total weight
  if (extractedStones.length === 1 && docTotalWeight) {
    const s = extractedStones[0];
    const qty = parseInt(s.quantity, 10);
    if (qty > 0) {
      s.weight = (docTotalWeight / qty).toFixed(4);
    }
  }

  return extractedStones;
};

export function StoneImportDialog({ open, onOpenChange, onImport, existingStones = [] }) {
  const [rows, setRows] = useState([]);
  const [activeCell, setActiveCell] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [useLocalOcr, setUseLocalOcr] = useState(false);
  const [ocrStatus, setOcrStatus] = useState('');
  const gridRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const existing = existingStones.map(stoneToRow);
      const empties = Array.from({ length: Math.max(3, 8 - existing.length) }, emptyRow);
      setRows([...existing, ...empties]);
    }
  }, [open, existingStones]);

  const updateCell = (rowIdx, field, value) => {
    setRows(prev => prev.map((r, i) => i === rowIdx ? { ...r, [field]: value } : r));
  };

  const removeRow = (idx) => {
    setRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  const addRows = (count = 3) => {
    setRows(prev => [...prev, ...Array.from({ length: count }, emptyRow)]);
  };

  const runLocalOcr = async (base64Data) => {
    try {
      setOcrStatus("Loading OCR library...");
      const tesseract = await loadTesseract();
      
      setOcrStatus("Enhancing image quality...");
      const processedImage = await preprocessImage(base64Data);
      
      setOcrStatus("Initializing OCR engine...");
      const worker = await tesseract.createWorker('eng');
      
      setOcrStatus("Scanning image for text...");
      const ret = await worker.recognize(processedImage);
      const text = ret.data.text;
      
      setOcrStatus("Parsing stone data...");
      await worker.terminate();
      
      return parseOcrText(text);
    } catch (err) {
      console.error("Local OCR failed:", err);
      throw new Error("Local OCR failed: " + err.message);
    }
  };

  const processScreenshot = async (base64Data) => {
    try {
      setAnalyzing(true);
      
      let newStones = [];
      if (useLocalOcr) {
        newStones = await runLocalOcr(base64Data);
      } else {
        try {
          const res = await parseStonesScreenshot(base64Data);
          newStones = res.data?.stones || [];
        } catch (backendErr) {
          console.warn("Backend parsing failed, falling back to local OCR:", backendErr);
          toast.info("AI not configured. Falling back to local offline OCR...");
          newStones = await runLocalOcr(base64Data);
        }
      }

      if (newStones.length === 0) {
        toast.error("No stones found in screenshot.");
        return;
      }
      
      const newRows = newStones.map(s => ({
        key: crypto.randomUUID(),
        type: s.type || 'Diamond',
        ownership: s.ownership || 'Ours/Cust',
        cut: s.cut || 'Round',
        category: s.category || 'Accent Stone',
        quality: s.quality || '',
        size: s.size || '',
        weight: s.weight || '',
        quantity: s.quantity || '',
      }));

      setRows(prev => {
        // filter out any empty rows at the end to keep it clean
        const activeRows = prev.filter(r => r.type || r.ownership || r.cut || r.category || r.quality || r.size || r.weight || r.quantity);
        return [...activeRows, ...newRows];
      });
      
      toast.success(`Successfully extracted ${newStones.length} stone row(s) from screenshot!`);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to analyze screenshot"));
    } finally {
      setAnalyzing(false);
      setOcrStatus('');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      await processScreenshot(event.target.result);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        const reader = new FileReader();
        reader.onload = async (event) => {
          await processScreenshot(event.target.result);
        };
        reader.readAsDataURL(file);
        e.preventDefault();
        return;
      }
    }

    const text = e.clipboardData.getData('text');
    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length <= 1 && !lines[0]?.includes('\t')) return;
    e.preventDefault();
    const startRow = activeCell?.row ?? 0;
    const startCol = activeCell?.col ?? 0;
    setRows(prev => {
      const newRows = [...prev];
      lines.forEach((line, li) => {
        const cols = line.split('\t');
        const ri = startRow + li;
        while (newRows.length <= ri) newRows.push(emptyRow());
        cols.forEach((val, ci) => {
          const fi = startCol + ci;
          if (fi < FIELDS.length) newRows[ri][FIELDS[fi]] = val.trim();
        });
      });
      return newRows;
    });
  }, [activeCell]);

  const handleKeyDown = useCallback((e, rowIdx, colIdx) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      const nextCol = e.shiftKey ? colIdx - 1 : colIdx + 1;
      if (nextCol >= 0 && nextCol < FIELDS.length) {
        setActiveCell({ row: rowIdx, col: nextCol });
        const cell = gridRef.current?.querySelector(`[data-row="${rowIdx}"][data-col="${nextCol}"]`);
        cell?.focus();
      } else if (!e.shiftKey && nextCol >= FIELDS.length && rowIdx + 1 < rows.length) {
        setActiveCell({ row: rowIdx + 1, col: 0 });
        const cell = gridRef.current?.querySelector(`[data-row="${rowIdx + 1}"][data-col="0"]`);
        cell?.focus();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (rowIdx + 1 < rows.length) {
        setActiveCell({ row: rowIdx + 1, col: colIdx });
        const cell = gridRef.current?.querySelector(`[data-row="${rowIdx + 1}"][data-col="${colIdx}"]`);
        cell?.focus();
      }
    } else if (e.key === 'ArrowDown' && rowIdx + 1 < rows.length) {
      setActiveCell({ row: rowIdx + 1, col: colIdx });
      const cell = gridRef.current?.querySelector(`[data-row="${rowIdx + 1}"][data-col="${colIdx}"]`);
      cell?.focus();
    } else if (e.key === 'ArrowUp' && rowIdx > 0) {
      setActiveCell({ row: rowIdx - 1, col: colIdx });
      const cell = gridRef.current?.querySelector(`[data-row="${rowIdx - 1}"][data-col="${colIdx}"]`);
      cell?.focus();
    }
  }, [rows.length]);

  const handleImport = () => {
    const valid = rows.filter(r => r.type || r.cut || r.quality || r.weight || r.quantity || r.size || r.ownership);
    if (valid.length === 0) return;
    onImport(valid.map(r => ({
      type: r.type || 'Diamond', ownership: r.ownership, cut: r.cut, category: r.category || '',
      quality: r.quality, weight: r.weight, quantity: r.quantity, size: r.size,
    })));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col p-0">
        {analyzing && (
          <div className="absolute inset-0 bg-background/60 backdrop-blur-sm flex flex-col items-center justify-center z-50">
            <Loader2 className="h-8 w-8 text-primary animate-spin mb-2" />
            <p className="text-sm font-medium">{ocrStatus || "Analyzing screenshot with AI..."}</p>
          </div>
        )}
        <DialogHeader className="px-5 pt-5 pb-2">
          <DialogTitle>Bulk Add / Edit Stones</DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Existing stones are pre-loaded. Edit, add, paste from Excel/Sheets, or paste/upload a screenshot of a CAD report.
          </DialogDescription>
        </DialogHeader>
        <div ref={gridRef} className="flex-1 overflow-auto px-5" onPaste={handlePaste}>
          <div className="border border-border rounded-md overflow-hidden">
            <div className="grid grid-cols-[140px_100px_1fr_1fr_80px_80px_60px_70px_80px_32px] bg-muted/60 border-b border-border">
              {HEADERS.map(h => (
                <div key={h} className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-r border-border last:border-r-0">{h}</div>
              ))}
              <div />
            </div>
            {rows.map((row, ri) => (
              <div key={row.key} className="grid grid-cols-[140px_100px_1fr_1fr_80px_80px_60px_70px_80px_32px] border-b border-border/50 last:border-b-0 hover:bg-muted/20">
                <div className="border-r border-border/50 p-0">
                  <input data-row={ri} data-col={0} className="w-full h-8 px-2 text-xs bg-transparent outline-none focus:ring-1 focus:ring-inset focus:ring-ring" value={row.type} onChange={e => updateCell(ri, 'type', e.target.value)} onFocus={() => setActiveCell({ row: ri, col: 0 })} onKeyDown={e => handleKeyDown(e, ri, 0)} placeholder="Diamond" list="stone-types-list" />
                </div>
                {FIELDS.slice(1).map((field, ci) => (
                  <div key={field} className="border-r border-border/50">
                    <input data-row={ri} data-col={ci + 1} className="w-full h-8 px-2 text-xs bg-transparent outline-none focus:ring-1 focus:ring-inset focus:ring-ring" value={row[field]} onChange={e => updateCell(ri, field, e.target.value)} onFocus={() => setActiveCell({ row: ri, col: ci + 1 })} onKeyDown={e => handleKeyDown(e, ri, ci + 1)} placeholder={['', 'Ours/Cust', 'Round', '', 'VS1', '3.0mm', '0.50', '4'][ci + 1] || ''} />
                  </div>
                ))}
                <div className="border-r border-border/50 flex items-center px-2 text-xs text-muted-foreground">
                  {((parseFloat(row.weight) || 0) * (parseInt(row.quantity) || 0)).toFixed(3)}
                </div>
                <div className="flex items-center justify-center">
                  <button className="p-1 text-muted-foreground hover:text-destructive transition-colors" onClick={() => removeRow(ri)} tabIndex={-1}>
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <datalist id="stone-types-list">
            {STONE_TYPES.map(t => <option key={t} value={t} />)}
          </datalist>
          <div className="flex items-center gap-4 mt-2">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => addRows()} className="text-xs h-7">
                <Plus className="h-3 w-3 mr-1" /> Add Rows
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="text-xs h-7 border-dashed border-primary/40 hover:border-primary/80"
                disabled={analyzing}
              >
                {analyzing ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Upload className="h-3 w-3 mr-1" />
                )}
                {analyzing ? "Analyzing..." : "Upload/Paste Screenshot"}
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
            </div>
            
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none cursor-pointer">
              <input
                type="checkbox"
                checked={useLocalOcr}
                onChange={(e) => setUseLocalOcr(e.target.checked)}
                className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5 bg-transparent"
              />
              Local OCR (Offline/No AI)
            </label>
          </div>
        </div>
        <DialogFooter className="px-5 pb-5 pt-3 border-t border-border">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button size="sm" onClick={handleImport} data-testid="cad-import-stones-btn">Save All Stones</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
